import { useState, useCallback } from 'react';
import { Status } from 'hooks/requests/fetchUtils';
import useFetch from 'hooks/useFetch';
import Coords from 'state/Coords';
import { lensPath, view } from 'ramda';

const PATH = 'https://maps.googleapis.com/maps/api/geocode/json';

type GeocodingHook = () => [
  (address: string) => Promise<Coords | undefined>,
  Status,
  string | null,
];

export class GeocoderError extends Error {}

export const useGeocoder: GeocodingHook = () => {
  const [error, setError] = useState<string | null>(null);
  const [status, fetch] = useFetch();

  const geocode = useCallback(
    async (address: string): Promise<Coords | undefined> => {
      setError(null);

      const response = await fetch(
        `${PATH}?address=${address}&key=${process.env.REACT_APP_GEOCODING_KEY}`,
        {
          method: 'GET',
          headers: {},
        },
      );

      const body = await response.json();
      if (response.ok) {
        const location = view(
          lensPath(['results', 0, 'geometry', 'location']),
          body,
        ) as { lat: number; lng: number } | undefined;

        return Promise.resolve(
          location && {
            latitude: location.lat,
            longitude: location.lng,
          },
        );
      } else {
        const error = body.error_message;
        setError(error);
        return Promise.reject(new GeocoderError(error));
      }
    },
    [fetch],
  );

  return [geocode, status, error];
};

export default useGeocoder;
